import { http } from '@kunlun/dependencies';

/**
 * 销售占比管控配置 —— 创建向导所需的选项查询与批量保存接口
 * 所有查询/提交均通过原生 GraphQL（http.query / http.mutate）完成。
 * moduleName 由调用方在运行时传入（this.model.moduleName）。
 */

export interface ChannelTypeOption {
  code: string;
  name: string;
}

export interface BrandStoreOption {
  code: string;
  name: string;
}

export interface SalesOfficeOption {
  code: string;
  name: string;
  displayName?: string;
  brandStoreId?: string;
}

export interface CustomerOption {
  code: string;
  name: string;
  erpCode?: string;
  provincialBranchCode?: string;
  provincialBranch?: { code?: string; name?: string };
  channelTypes?: { name: string }[];
  salesOffices?: { code: string; name: string }[];
}

/** 合作关系（一个客户对应多个店铺/销售办公室） */
export interface RelationApplyOption {
  targetPartnerCode?: string;
  ariesTargetPartnerName?: string;
  customerErpCode?: string;
  originPartnerCode?: string;
  originPartner?: { code?: string; name?: string };
  salesOffice?: { code?: string; name?: string };
  salesOrganization?: { name?: string };
  customerChannelName?: string;
}

/** 客户选择弹窗：合作关系分页结果（后端已排除无合作关系客户并回填渠道） */
export interface CustomerRelationPageResult {
  content: RelationApplyOption[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
}

/** 扁平化后的一条配置记录（与后端 DeliAriesPlatformSalesRatioControlConfig 对齐） */
export interface SalesRatioControlRecord {
  controlLevel: 'CHANNEL' | 'PROVINCE' | 'CUSTOMER';
  channelType?: string;
  channelTypeCode?: string;
  provincialBranchCode?: string;
  provincialBranchName?: string;
  salesOfficeCode?: string;
  salesOfficeName?: string;
  customerCode?: string;
  customerErpCode?: string;
  customerName?: string;
  promoSalesRatio?: number | string | null;
  customerMarginRatio?: number | string | null;
  provinceMarginRatio?: number | string | null;
  oldProductMarginRate?: number | string | null;
  newProductMarginRate?: number | string | null;
}

const PAGE = 'page: { currentPage: 1, size: 200 }';

function likeClause(fields: string[], keyword?: string): string {
  const kw = (keyword || '').trim();
  if (!kw) {
    return '(1==1)';
  }
  const parts = fields.map((f) => `${f}=like='%${kw}%'`);
  return `(1==1) and (${parts.join(' or ')})`;
}

/** 渠道类型 */
export async function queryChannelTypes(moduleName: string, keyword?: string): Promise<ChannelTypeOption[]> {
  const gql = `
    query {
      ariesPlatformChannelTypeQuery {
        queryPage(
          ${PAGE}
          queryWrapper: { rsql: "${likeClause(['code', 'name'], keyword)}", queryData: "{}" }
        ) {
          content { code name }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.ariesPlatformChannelTypeQuery?.queryPage?.content || [];
}

/** 省区（品牌店铺） */
export async function queryBrandStores(moduleName: string, keyword?: string): Promise<BrandStoreOption[]> {
  const gql = `
    query {
      deliAriesPlatformBrandStoreQuery {
        queryPage(
          ${PAGE}
          queryWrapper: { rsql: "${likeClause(['code', 'name'], keyword)}", queryData: "{}" }
        ) {
          content { code name }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformBrandStoreQuery?.queryPage?.content || [];
}

/** 销售办公室（可按省区/品牌店铺 code 过滤） */
export async function querySalesOffices(
  moduleName: string,
  params: { brandStoreCode?: string; keyword?: string } = {}
): Promise<SalesOfficeOption[]> {
  const clauses: string[] = ['(1==1)'];
  const kw = (params.keyword || '').trim();
  if (params.brandStoreCode) {
    clauses.push(`(brandStore.code=='${params.brandStoreCode}')`);
  }
  if (kw) {
    clauses.push(`((code=like='%${kw}%') or (name=like='%${kw}%') or (displayName=like='%${kw}%'))`);
  }
  const rsql = clauses.join(' and ');
  const gql = `
    query {
      deliAriesPlatformSalesOfficeQuery {
        queryPage(
          ${PAGE}
          queryWrapper: { rsql: "${rsql}", queryData: "{}" }
        ) {
          content { code name displayName brandStoreId }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformSalesOfficeQuery?.queryPage?.content || [];
}

/** 客户（经销商），可按省区/渠道/关键字过滤，支持分页 */
export interface CustomerPageResult {
  content: CustomerOption[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
}

export async function queryCustomers(
  moduleName: string,
  params: { province?: string; channel?: string; keyword?: string; currentPage?: number; size?: number } = {}
): Promise<CustomerPageResult> {
  const clauses: string[] = ['(1==1)'];
  const kw = (params.keyword || '').trim();
  if (params.province) {
    clauses.push(`(provincialBranch.name=like='%${params.province}%')`);
  }
  if (kw) {
    clauses.push(`((code=like='%${kw}%') or (name=like='%${kw}%'))`);
  }
  const rsql = clauses.join(' and ');
  const currentPage = params.currentPage && params.currentPage > 0 ? params.currentPage : 1;
  const size = params.size && params.size > 0 ? params.size : 15;
  const pageClause = `page: { currentPage: ${currentPage}, size: ${size} }`;
  const gql = `
    query {
      deliAriesPlatformCustomerDistributorQuery {
        queryPage(
          ${pageClause}
          queryWrapper: { rsql: "${rsql}", queryData: "{}" }
        ) {
          totalPages
          totalElements
          content {
            code
            name
            erpCode
            provincialBranchCode
            provincialBranch { code name }
            channelTypes { name }
            salesOffices { code name }
          }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  const page = res?.data?.deliAriesPlatformCustomerDistributorQuery?.queryPage;
  return {
    content: page?.content || [],
    totalPages: page?.totalPages || 0,
    totalElements: page?.totalElements || 0,
    currentPage
  };
}

/** 按 ERP 编码批量查询客户信息（用于导入时根据 ERP 编码补全客户名称） */
export async function queryCustomersByErpCodes(moduleName: string, erpCodes: string[]): Promise<CustomerOption[]> {
  const codes = (erpCodes || []).map((c) => (c || '').trim()).filter(Boolean);
  if (codes.length === 0) return [];
  const inClause = codes.map((c) => `'${c}'`).join(',');
  const size = Math.max(codes.length, 1);
  const gql = `
    query {
      deliAriesPlatformCustomerDistributorQuery {
        queryPage(
          page: { currentPage: 1, size: ${size} }
          queryWrapper: { rsql: "erpCode=in=(${inClause})", queryData: "{}" }
        ) {
          content {
            code
            name
            erpCode
          }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformCustomerDistributorQuery?.queryPage?.content || [];
}

/** 按客户编码查询其合作关系（每条对应一个店铺=省区 + 销售办公室） */
export async function queryRelationApplies(moduleName: string, customerCode: string): Promise<RelationApplyOption[]> {
  const code = (customerCode || '').trim();
  if (!code) {
    return [];
  }
  return queryRelationAppliesByCustomers(moduleName, [code]);
}

/** 批量按多个客户编码查询合作关系（一次请求） */
export async function queryRelationAppliesByCustomers(
  moduleName: string,
  customerCodes: string[]
): Promise<RelationApplyOption[]> {
  const codes = (customerCodes || []).map((c) => (c || '').trim()).filter(Boolean);
  if (codes.length === 0) {
    return [];
  }
  const inClause = codes.map((c) => `'${c}'`).join(',');
  const gql = `
    query {
      deliAriesPlatformRelationApplyQuery {
        queryPage(
          ${PAGE}
          queryWrapper: { rsql: "targetPartnerCode=in=(${inClause})", queryData: "{}" }
        ) {
          content {
            targetPartnerCode
            originPartnerCode
            originPartner { code name }
            salesOffice { code name }
            salesOrganization { name }
          }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformRelationApplyQuery?.queryPage?.content || [];
}

/**
 * 客户选择：直接分页查询合作关系（后端已天然排除无合作关系的客户，并回填客户渠道名称）。
 * 每条记录 = 一个客户在一个店铺（省区）下的合作关系。
 */
export async function queryCustomerRelationPage(
  moduleName: string,
  params: { province?: string; keyword?: string; currentPage?: number; size?: number } = {}
): Promise<CustomerRelationPageResult> {
  const clauses: string[] = ['(1==1)'];
  const kw = (params.keyword || '').trim();
  if (params.province) {
    clauses.push(`(originPartner.name=like='%${params.province}%')`);
  }
  if (kw) {
    clauses.push(`((targetPartnerCode=like='%${kw}%') or (targetPartner.name=like='%${kw}%'))`);
  }
  const rsql = clauses.join(' and ');
  const currentPage = params.currentPage && params.currentPage > 0 ? params.currentPage : 1;
  const size = params.size && params.size > 0 ? params.size : 15;
  const pageClause = `page: { currentPage: ${currentPage}, size: ${size} }`;
  const gql = `
    query {
      deliAriesPlatformRelationApplyQuery {
        queryCustomerRelationPage(
          ${pageClause}
          queryWrapper: { rsql: "${rsql}", queryData: "{}" }
        ) {
          totalPages
          totalElements
          content {
            targetPartnerCode
            ariesTargetPartnerName
            customerErpCode
            originPartnerCode
            originPartner { code name }
            salesOffice { code name }
            customerChannelName
          }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  const page = res?.data?.deliAriesPlatformRelationApplyQuery?.queryCustomerRelationPage;
  return {
    content: page?.content || [],
    totalPages: page?.totalPages || 0,
    totalElements: page?.totalElements || 0,
    currentPage
  };
}

function serializeRecord(r: SalesRatioControlRecord): string {
  const parts: string[] = [];
  // 枚举：不加引号
  parts.push(`controlLevel: ${r.controlLevel}`);
  const strField = (key: keyof SalesRatioControlRecord, val?: string) => {
    if (val !== undefined && val !== null && `${val}`.length > 0) {
      parts.push(`${key}: ${JSON.stringify(val)}`);
    }
  };
  const numField = (key: keyof SalesRatioControlRecord, val: number | string | null | undefined) => {
    if (val !== undefined && val !== null && `${val}`.length > 0) {
      parts.push(`${key}: ${val}`);
    }
  };
  strField('channelType', r.channelType);
  strField('channelTypeCode', r.channelTypeCode);
  strField('provincialBranchCode', r.provincialBranchCode);
  strField('provincialBranchName', r.provincialBranchName);
  strField('salesOfficeCode', r.salesOfficeCode);
  strField('salesOfficeName', r.salesOfficeName);
  strField('customerCode', r.customerCode);
  strField('customerErpCode', r.customerErpCode);
  strField('customerName', r.customerName);
  numField('promoSalesRatio', r.promoSalesRatio);
  numField('customerMarginRatio', r.customerMarginRatio);
  numField('provinceMarginRatio', r.provinceMarginRatio);
  numField('oldProductMarginRate', r.oldProductMarginRate);
  numField('newProductMarginRate', r.newProductMarginRate);
  return `{ ${parts.join(', ')} }`;
}

/** 批量保存（展开后的多条记录一次性提交） */
export async function batchSaveConfigs(moduleName: string, records: SalesRatioControlRecord[]): Promise<any[]> {
  const dataList = records.map(serializeRecord).join(', ');
  const gql = `
    mutation {
      deliAriesPlatformSalesRatioControlConfigMutation {
        batchSave(dataList: [ ${dataList} ]) {
          id
        }
      }
    }
  `;
  const res = await http.mutate<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformSalesRatioControlConfigMutation?.batchSave || [];
}

/**
 * 按 id 查询单条配置详情（显式字段子选择）。
 * 用于查看页（Detail 视图）自行加载数据，规避框架自动生成的空 queryOne 查询。
 */
export async function queryConfigById(moduleName: string, id: string): Promise<Record<string, any> | null> {
  const pk = (id || '').trim();
  if (!pk) {
    return null;
  }
  const gql = `
    query {
      deliAriesPlatformSalesRatioControlConfigQuery {
        queryOne(query: { id: ${JSON.stringify(pk)} }) {
          id
          controlLevel
          channelType
          channelTypeCode
          provincialBranchCode
          provincialBranchName
          salesOfficeCode
          salesOfficeName
          customerCode
          customerErpCode
          customerName
          promoSalesRatio
          customerMarginRatio
          provinceMarginRatio
          oldProductMarginRate
          newProductMarginRate
          writeDate
          customerDistributor { id code name }
        }
      }
    }
  `;
  const res = await http.query<any>(moduleName, gql);
  return res?.data?.deliAriesPlatformSalesRatioControlConfigQuery?.queryOne || null;
}
